import { credentialApi } from '@/api';
import { Modal } from '@/components/common';
import { getDefaultTablePaginationConfig, handleResponse, ScrollType } from '@/Helper';
import { SecuredComponents, User } from '@/model/AccountModel';
import { BaseColumns, ResponseObjectEntity } from '@/model/CommonModel';
import { MasterDoorGroupDoorGroup } from '@/model/CredentialModel';
import { Button, Table, TableColumnsType, TablePaginationConfig, TableProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { DoorGroupInfoTable } from '../DoorGroupInfoTable';
import styles from './masterDoorGroupInfoModal.module.scss';

type DoorGroupsDataSource = MasterDoorGroupDoorGroup & BaseColumns;
type Props = {
	id: number;
	name: string;
	onClose: () => void;
};

const user: User = getUser();
const doorGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Door_Groups);
//Avoid creating object style inline, since increases reconciliations
const doorGroupScroll: ScrollType = { y: 150 };
const readerScroll: ScrollType = { y: 160 };

export const MasterDoorGroupInfoModal: React.FC<Props> = ({ id, name, onClose }) => {
	//#region State Management Variables
	const [isLoading, setIsLoading] = useState(true);
	const [doorGroups, setDoorGroups] = useState<DoorGroupsDataSource[]>([]);
	const [selectedDoorGroup, setSelectedDoorGroup] = useState<MasterDoorGroupDoorGroup>({ id: 0, name: '', hasPermission: false });
	//#endregion

	useEffect(() => {
		credentialApi
			.getMasterDoorGroupDoorGroups(id)
			.then((response: ResponseObjectEntity<MasterDoorGroupDoorGroup[]>) => {
				if (!handleResponse(response)) {
					setDoorGroups(response.Entity.map((doorGroups: MasterDoorGroupDoorGroup) => ({ ...doorGroups, key: doorGroups.id })));
					if (response.Entity?.length > 0) {
						setSelectedDoorGroup(response.Entity[0]);
					}
				} else {
					onClose();
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	const selectedRowKeys = selectedDoorGroup.id === 0 ? [] : [selectedDoorGroup.id];

	const rowSelection: TableProps<DoorGroupsDataSource>['rowSelection'] = {
		selectedRowKeys: selectedRowKeys,
		onChange: (selectedRowKeys: React.Key[], selectedRows: DoorGroupsDataSource[]) => {
			if (selectedRowKeys?.length > 0) {
				setSelectedDoorGroup(selectedRows[0]);
			}
		},
		getCheckboxProps: (record: DoorGroupsDataSource) => ({
			id: `doorGroup-${record.key}`,
			children: <label htmlFor={`doorGroup-${record.key}`} className={styles.srOnly}>{`${_('SelectItem')}`}</label>,
		}),
		type: 'radio',
	};

	const columns: TableColumnsType<DoorGroupsDataSource> = [
		{
			title: _('Name'),
			dataIndex: 'name',
		},
	];

	const footer = [
		<Button id='masterDoorGroupInfoCancel' onClick={onClose} key='cancel'>
			{_('Cancel')}
		</Button>,
	];

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(false, undefined, undefined, 10, doorGroups.length, false, selectedRowKeys);
	const title = `${name} - ${_('MasterDoorGroup')}`;

	return (
		<Modal destroyOnClose visible footer={footer} onCancel={onClose} title={title} width='950px' customZoomClass={styles.masterDoorGroupModal}>
			<div>
				<div>
					<Table<DoorGroupsDataSource>
						className={styles.doorGroupsTable}
						showHeader={false}
						title={() => <strong>{_('DoorGroups')}</strong>}
						rowSelection={rowSelection}
						columns={columns}
						dataSource={doorGroups}
						pagination={pagination}
						loading={isLoading}
						size='small'
						scroll={doorGroupScroll}
						rowClassName={(record, index) => index % 2 != 0 && styles.grayRow}
					/>
				</div>
				<div className={styles.instructions}>
					<strong>{selectedDoorGroup.name}</strong>
					<p>Select a Door Group in the section above to view its content</p>
				</div>
				<div>
					<DoorGroupInfoTable
						doorGroupId={selectedDoorGroup.id}
						hasPermission={selectedDoorGroup.hasPermission && selectedDoorGroup.id != 0 && doorGroupComponentPermission.canView}
						scroll={readerScroll}
						hideExpandColumn
					/>
				</div>
			</div>
		</Modal>
	);
};
